import { Component, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectionStrategy, OnChanges } from '@angular/core';
import { ModuleService } from '../../../resources/module/module.service';
import { Subject } from 'rxjs';
import { ElementService } from '../../../resources/element/element.service';
import { ProjectElement } from '../../../resources/element/project-element.interface';
import { ProjectSubmodule } from '../../../resources/submodule/project-submodule.interface';
import { ProjectModule } from '../../../resources/module/project-module.interface';
import { CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { SubmoduleService } from '../../../resources/submodule/submodule.service';
import { ProjectInfoService } from '../project-info.service';
import { map, takeUntil } from 'rxjs/operators';
import { ScrollToService } from '../../../utilities/scroll-to/scroll-to.service';
import { IndexView } from '../project-info.interface';
import { AuthService } from '../../../auth/auth.service';
import { ProjectService } from '../../../resources/project/project.service';

/**
 * Document tree.
 */
@Component({
  selector: 'sg2-project-info-index',
  templateUrl: './project-info-index.component.html',
  styleUrls: ['./project-info-index.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectInfoIndexComponent implements OnDestroy, OnInit {

  /** Reference to view element. */
  @ViewChild('index', { static: true }) indexRef: ElementRef;

  /** Destroy hook. */
  private destroy$ = new Subject<any>();

  /** List of element droplists for drag-dropping between different submodules. */
  elementDropListIds: string[];

  /** Current index view. */
  public view: IndexView;

  constructor(
    public authService: AuthService,
    public elementService: ElementService,
    public moduleService: ModuleService,
    private projectService: ProjectService,
    public projectInfoService: ProjectInfoService,
    private scrollToService: ScrollToService,
    public submoduleService: SubmoduleService
  ) { }

  ngOnInit() {
    this._onVisibleElementsChange();
    this._onIndexViewChange();
    this._onSubmodulesChange();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Scroll to entity.
   *
   * @param entity The entity to scroll to.
   */
  public scrollTo(entity: ProjectElement | ProjectModule | ProjectSubmodule): void {
    this.scrollToService.find(entity._id);
  }

  /**
   * Order elements after drag and drop.
   *
   * @param submodule The parent submodule.
   * @param event The dispatched event after dropping.
   */
  public orderElements(event: CdkDragDrop<String[]>): void {
    const element = event.item.data as ProjectElement;
    this.elementService.move(element,
      { index: event.previousIndex, submodule: this._getSubmoduleIdFromContainer(event.previousContainer) },
      { index: event.currentIndex, submodule: this._getSubmoduleIdFromContainer(event.container) }
    );
  }

  /**
   * Order modules after drag and drop.
   *
   * @param event The dispatched event after dropping.
   */
  public orderModules(event: CdkDragDrop<String[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      this.moduleService.setOrder(event.item.data, event.currentIndex);
    }
  }

  /**
   * Order submodules after drag and drop.
   *
   * @param module The parent module.
   * @param event The dispatched event after dropping.
   */
  public orderSubmodules(event: CdkDragDrop<String[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      this.submoduleService.setOrder(event.item.data, event.currentIndex);
    }
  }

  /**
   * Get the submodule's id from a dragdrop-container's HTML element id.
   *
   * @returns Subodule id extracted from container data.
   */
  private _getSubmoduleIdFromContainer(container: CdkDropList<String[]>): ProjectSubmodule['_id'] {
    return container.id.split('-')[2];
  }

  /**
   * Create list of all element droplist ids.
   *
   * @param submodules List of all (active) submodules.
   */
  private _setElementDropListIds(submodules: ProjectSubmodule[]): void {
    const ids = [];
    for (const submodule of submodules) {
      ids.push(`element-droplist-${ submodule._id }`);
    }
    this.elementDropListIds = ids;
  }

  /**
   * Add a class to currently visible elements.
   *
   * @param elements List of id's of currently visible elements.
   */
  private _setVisibleElements(elements: ProjectElement['_id'][]): void {
    const els = this.indexRef.nativeElement.querySelectorAll('.element') as HTMLElement[];
    Array.from(els).forEach(el => {
      if (elements.length && elements[0].indexOf(el.getAttribute('id').replace('index-', '')) > -1) {
        el.classList.add('visible');
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest'});
      } else {
        el.classList.remove('visible');
      }
    });
  }

  /**
   * Track changes in index view.
   *
   * - Show modules.
   * - Show modules and submodules (not implemented).
   * - Show modules, submodules and elements.
   */
  private _onIndexViewChange(): void {
    this.projectInfoService.view$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(view => {
      this.view = view;
    });
  }

  /**
   * Track currently visible elements in the document.
   */
  private _onVisibleElementsChange(): void {
    this.projectInfoService.visibleElement$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(elements => {
      this._setVisibleElements(elements);
    });
  }

  /**
   * Track changes in submodules.
   */
  private _onSubmodulesChange(): void {
    this.submoduleService.submodule$.pipe(
      takeUntil(this.destroy$),
      map(submodule => submodule.filter(sm => sm.active && !sm.isTechnicalType)),
    ).subscribe(submodules => {
      this._setElementDropListIds(submodules);
    });
  }
}
